import { useState, useCallback, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { sendMessage } from '../services/chatService';
import { CHAT_CONFIG } from '../services/chatConfig';
import type { ChatMessage, ChatError, ChatState } from '../services/chatTypes';

const INITIAL_STATE: ChatState = {
  messages: [],
  isLoading: false,
  isStreaming: false,
  error: null,
  retryCount: 0,
  draftMessage: '',
};

/**
 * Core hook managing chat message state, submission, retry, streaming, and error recovery.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.6, 4.2, 4.5, 6.1, 6.2, 6.3, 6.6
 */
export function useChat() {
  const [state, setState] = useState<ChatState>(INITIAL_STATE);

  // Track the streaming assistant message ID so we can update it as tokens arrive
  const streamingMessageIdRef = useRef<string | null>(null);

  // AbortController for the current in-flight request (allows cancellation on unmount/navigation)
  const abortControllerRef = useRef<AbortController | null>(null);

  // Abort any in-flight request on unmount (e.g., user navigates away)
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  /**
   * Submits a user message to the chat service.
   * - Validates input (rejects empty/whitespace and > 2000 chars)
   * - Adds user message to state
   * - Calls sendMessage with streaming callbacks
   * - On error, preserves draft and marks user message as 'error'
   */
  const submitMessage = useCallback((message: string) => {
    // Validate: reject empty/whitespace
    const trimmed = message.trim();
    if (trimmed.length === 0) {
      return;
    }

    // Validate: reject > 2000 characters
    if (message.length > CHAT_CONFIG.maxMessageLength) {
      return;
    }

    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: 'user',
      content: message,
      timestamp: Date.now(),
      status: 'sent',
    };

    // Create a placeholder assistant message for streaming
    const assistantMessageId = uuidv4();
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      status: 'streaming',
    };

    streamingMessageIdRef.current = assistantMessageId;

    setState((prev) => ({
      ...prev,
      messages: [...prev.messages, userMessage, assistantMessage],
      isLoading: true,
      isStreaming: false,
      error: null,
      draftMessage: '',
    }));

    // Build conversation history excluding the new messages we just added.
    // The sendMessage function will receive the history of prior messages.
    // We need to get the current messages before we added the new ones.
    // Since setState is async, we capture the history from prev state via a separate ref approach.
    // Instead, sendMessage receives conversationHistory — we pass the messages that were
    // in state before this submission (excluding error messages, handled by chatHistory helper).
    const conversationHistory = state.messages;

    // Create an AbortController for this request (allows timeout and cancellation on unmount)
    const controller = new AbortController();
    abortControllerRef.current = controller;

    sendMessage(
      message,
      conversationHistory,
      {
        onToken: (token: string) => {
          setState((prev) => {
            const updatedMessages = prev.messages.map((msg) => {
              if (msg.id === assistantMessageId) {
                return { ...msg, content: msg.content + token };
              }
              return msg;
            });
            return {
              ...prev,
              messages: updatedMessages,
              isStreaming: true,
            };
          });
        },
        onComplete: (_fullResponse: string, _stopReason: string) => {
          streamingMessageIdRef.current = null;
          abortControllerRef.current = null;
          setState((prev) => {
            const updatedMessages = prev.messages.map((msg) => {
              if (msg.id === assistantMessageId) {
                return { ...msg, status: 'complete' as const };
              }
              return msg;
            });
            return {
              ...prev,
              messages: updatedMessages,
              isLoading: false,
              isStreaming: false,
              retryCount: 0,
            };
          });
        },
        onError: (error: ChatError) => {
          streamingMessageIdRef.current = null;
          abortControllerRef.current = null;
          setState((prev) => {
            // Preserve partial response content in the assistant message (don't clear content).
            // Mark both user message and assistant placeholder as 'error' with errorType.
            const updatedMessages = prev.messages.map((msg) => {
              if (msg.id === userMessage.id || msg.id === assistantMessageId) {
                return { ...msg, status: 'error' as const, errorType: error.type };
              }
              return msg;
            });
            return {
              ...prev,
              messages: updatedMessages,
              isLoading: false,
              isStreaming: false,
              error,
              draftMessage: message, // Preserve draft for retry/edit
            };
          });
        },
      },
      controller.signal
    );
  }, [state.messages]);

  /**
   * Retries the last user message.
   * - Enforces max 3 consecutive retries
   * - Increments retryCount and re-submits the last user message content
   */
  const retry = useCallback(() => {
    if (state.retryCount >= CHAT_CONFIG.maxRetries) {
      // Max retries reached — set error indicating user should try again later
      setState((prev) => ({
        ...prev,
        error: {
          type: 'service',
          message: 'Please try again later.',
          retryable: false,
        },
      }));
      return;
    }

    // Find the last user message (search from end)
    const lastUserMessage = [...state.messages]
      .reverse()
      .find((msg) => msg.role === 'user');

    if (!lastUserMessage) {
      return;
    }

    // Increment retry count before re-submitting
    setState((prev) => ({
      ...prev,
      retryCount: prev.retryCount + 1,
      error: null,
    }));

    // Remove the failed user message and its assistant placeholder from state
    // Then re-submit with the original content
    const messageContent = lastUserMessage.content;

    setState((prev) => {
      // Remove the last failed pair (user message with error + assistant with error)
      const messagesWithoutFailed = prev.messages.filter(
        (msg) => msg.status !== 'error' || (msg.id !== lastUserMessage.id)
      );
      // Also remove the associated assistant error message
      const cleanedMessages = messagesWithoutFailed.filter(
        (msg) => !(msg.role === 'assistant' && msg.status === 'error' &&
          msg.timestamp >= lastUserMessage.timestamp)
      );
      return {
        ...prev,
        messages: cleanedMessages,
      };
    });

    // Use setTimeout to ensure state has settled before re-submitting
    setTimeout(() => {
      submitMessage(messageContent);
    }, 0);
  }, [state.retryCount, state.messages, submitMessage]);

  /**
   * Resets the chat session to initial state.
   */
  const resetSession = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    streamingMessageIdRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  return {
    messages: state.messages,
    isLoading: state.isLoading,
    isStreaming: state.isStreaming,
    error: state.error,
    retryCount: state.retryCount,
    draftMessage: state.draftMessage,
    submitMessage,
    retry,
    resetSession,
  };
}
